import React, { useState, useEffect, useMemo } from 'react';
import { Breadcrumbs, Flex, Title, MultiSelect, Button, Select, UnstyledButton, Accordion, FileInput, Text, TextInput, Group, Modal, Box, Menu, Textarea, NumberInput, Tooltip } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronDown, ChevronUp } from '@geist-ui/icons';
import { useMediaQuery } from '@mantine/hooks';
import styles from './styles.module.scss';
import classNames from 'classnames';
import SitemapTable from  '../SitemapPage/sitemapTable';
import { Checkbox } from '@common/components';

interface Website {
  id: string;
  name: string;
  tags: string;
  homepage: string;
  favicon: string;
  url: string;
}

function ManageWebsite() {
  const navigate = useNavigate();
  const { companyId } = useParams<{ companyId: string }>();
  const [t] = useTranslation('account');
  const isMobile = useMediaQuery('(max-width: 768px)'); // Media query for mobile devices

  const [websites, setWebsites] = useState<Website[]>([
    { id: "site1", name: 'www.facebook.com', tags: "sport,nature,fitness", homepage: "Facebook", favicon: "https://static.xx.fbcdn.net/rsrc.php/yb/r/hLRJ1GG_y0J.ico", url: "https://facebook.com" },
    { id: "site2", name: 'www.linkedin.com', tags: "nature,dance", homepage: "Linkedin", favicon: "https://static.licdn.com/scds/common/u/images/logos/favicons/v1/favicon.ico", url: "https://www.linkedin.com" },
    { id: "site3", name: 'www.pinterest.com', tags: "football,wins", homepage: "Pinterest", favicon: "https://s.pinimg.com/webapp/favicon_48x48-7470a30d.png", url: "https://www.pinterest.com" },
    { id: "site4", name: 'www.telegram.org', tags: "sport", homepage: "Telegram", favicon: "https://telegram.org//img/favicon.ico", url: "https://www.telegram.org" },
  ]);
  const [allTags, setAllTags] = useState(Array.from(new Set(websites.flatMap(site => site.tags.split(',')))));
  const [filteredWebsites, setFilteredWebsites] = useState<Website[]>(websites);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [expandedRows, setExpandedRows] = useState<string[]>([]); // Track expanded rows
  const [expandedMobileSettings, setExpandedMobileSettings] = useState<string[]>([]); // Track mobile settings expansion

  useEffect(() => {
    if (selectedTags.length === 0) {
      setFilteredWebsites(websites);
    } else {
      setFilteredWebsites(websites.filter(site =>
        selectedTags.every(tag => site.tags.includes(tag))
      ));
    }
  }, [selectedTags, websites]);

  const toggleRow = (id: string) => {
    if (expandedRows.includes(id)) {
      setExpandedRows(expandedRows.filter(rowId => rowId !== id));
    } else {
      setExpandedRows([...expandedRows, id]);
    }
  };

  const toggleMobileSettings = (id: string) => {
    if (expandedMobileSettings.includes(id)) {
      setExpandedMobileSettings(expandedMobileSettings.filter(rowId => rowId !== id));
    } else {
      setExpandedMobileSettings([...expandedMobileSettings, id]);
    }
  };

  const items = useMemo(() => [
    { title: t('Companies'), href: -2 },
    { title: t('List Of Users'), href: -1 }
  ].map((item, index) => (
    <Link to={"#"} key={index} style={{ color: "black" }} onClick={() => navigate(Number(item?.href))}>
      {item.title}
    </Link>
  )), [t, navigate]);

  const [tags, setTags] = useState([
    { id: 1, name: 'Sport' },
    { id: 2, name: 'Social' },
    { id: 3, name: 'Event' },
    { id: 4, name: 'Culture' }
  ]);

  const [isModalOpen, setModalOpen] = useState(false);
  const [newTagName, setNewTagName] = useState('');

  const addTag = () => {
    if (newTagName) {
      const newTag = { id: Date.now(), name: newTagName }; // Generate a unique ID
      setTags(currentTags => [...currentTags, newTag]);
      setNewTagName('');
      setModalOpen(false); // Close the modal
    }
  };

  const deleteTag = (id: any) => {
    setTags(tags.filter(tag => tag.id !== id));
  };
  const [editTagId, setEditTagId] = useState(null);
  const [editTagName, setEditTagName] = useState('');

  const startEdit = (tag: any) => {
    setEditTagId(tag.id);
    setEditTagName(tag.name);
  };

  const stopEdit = () => {
    setEditTagId(null);
    setEditTagName('');
  };

  const handleRename = (id: any) => {
    setTags(tags.map(tag => tag.id === id ? { ...tag, name: editTagName } : tag));
    stopEdit();
  };

  const handleKeyDown = (event: any, id: any) => {
    if (event.key === 'Enter') {
      handleRename(id);
    }
  };
  const [notifications, setNotifications] = useState<boolean>(true);
  const [ignoreCase, setIgnoreCase] = useState(false);
  const [urlsToExclude, setUrlsToExclude] = useState('');
  const [mustNotHave, setMustNotHave] = useState('');
  const [mustHaveAll, setMustHaveAll] = useState('');
  const [mustHaveAny, setMustHaveAny] = useState('');
  const [mediaToExclude, setMediaToExclude] = useState('');
  const [mediaToInclude, setMediaToInclude] = useState('');
  const [robotOption, setRobotOption] = useState(true);

  const [useUrl, setUseUrl] = useState(true);
  const [useContent, setUseContent] = useState(false);
  const [discardFragments, setDiscardFragments] = useState(false);
  const [discardParameters, setDiscardParameters] = useState(false);
  const [parametersToUse, setParametersToUse] = useState('');
  const [parametersToDiscard, setParametersToDiscard] = useState('');
  const [useHashFunction, setUseHashFunction] = useState(false);
  const [customHashFunction, setCustomHashFunction] = useState('');

  const [waitAfterLoad, setWaitAfterLoad] = useState(0);
  const [pollInterval, setPollInterval] = useState(500);
  const [newConnections, setNewConnections] = useState<any>(2);
  const [openConnections, setOpenConnections] = useState<any>(2);
  const [allowInteractive, setAllowInteractive] = useState(false);

  const [javascriptCode, setJavascriptCode] = useState('');
  const [referrer, setReferrer] = useState('');
  const [restrictDomains, setRestrictDomains] = useState<any>([]);
  const [defaultPages, setDefaultPages] = useState<any>('index.htm');
  const [maxPages, setMaxPages] = useState<any>(1000);
  const [storePasses, setStorePasses] = useState(false);
  const [headlessMode, setHeadlessMode] = useState(false);

  const [scanRate, setScanRate] = useState<any>(1000); // default to 1000ms
  const [scanDelay, setScanDelay] = useState<any>(0);


  const [expandedSections, setExpandedSections] = useState<any>([]);
  const handleToggleSection = (sectionKey:any) => {
    setExpandedSections((current:any) =>
      current.includes(sectionKey)
        ? current.filter((key:any) => key !== sectionKey)
        : [...current, sectionKey]
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <Breadcrumbs separator="/" mt="xs">
          {items}
          <Link to={"#"}>{t('Websites')}</Link>
        </Breadcrumbs>
        <Flex justify={"space-between"} p={"sm"} pl={"0"} mt={"20px"} align="center">
          <Title className={styles['second-heading']} mb={'18px'} order={1}>
            List Sites
          </Title>
        </Flex>
        <MultiSelect
          data={allTags}
          placeholder="Filter by Labels"
          value={selectedTags}
          onChange={setSelectedTags}
          className={styles['tag-filter']}
        />

        {isMobile ? (
          <div className={styles.mobileTableWrapper}>
            {filteredWebsites.map((site) => (
              <div key={site.id} className={styles.mobileCard}>
                <div className={styles.faviconContainer}>
                  <img src={site.favicon} alt="favicon" className={styles.favicon} />
                </div>
                <div className={styles.mobileDetails}>
                  <div><strong>{site.name}</strong></div>
                  <div>{site.homepage}</div>
                  <div>
                    <a href={site.url} target="_blank" rel="noopener noreferrer" className={styles.urlLink}>
                      {site.url}
                    </a>
                  </div>
                  <Button variant="outline" color="gray">
                    Audit
                  </Button>
                  {/* <UnstyledButton className={styles.circelBorder} style={expandedMobileSettings.includes(site.id) ? { backgroundColor: "#5b5eff", color: "white" } : undefined} aria-label={`Click here for more settings about 
                      ${site?.name}`}  onClick={() => toggleMobileSettings(site.id)}>
                    { expandedMobileSettings.includes(site.id) ?  : }
                  </UnstyledButton> */}

                  <Button variant="outline" color="blue" onClick={() => toggleMobileSettings(site.id)} >
                    {expandedMobileSettings.includes(site.id) ? <><ChevronUp/> Hide Settings</> : <><ChevronDown /> Show Settings</> }
                  </Button>
                </div>
                
                {/* Mobile Settings Expansion */}
                {expandedMobileSettings.includes(site.id) && (
                <>
                  <div className={styles.mobileSettings}>
                    <h3>Settings</h3>
                    <TextInput
                      label="Name"
                      placeholder="Edit website name"
                      defaultValue={site.homepage}
                      className={styles.settingSelect}
                    />
                    <Button variant="outline" className={styles.settingButton}>
                      Rescan {site.homepage}
                    </Button>
                    <Button variant="outline" className={classNames(styles.settingButton,styles.settingButtonSitemap)}>
                      Manage Sitemap
                    </Button>
                    <Select
                      label="Change Label"
                      placeholder="Add or edit Label"
                      data={allTags}
                      className={styles.settingSelect}
                      defaultValue={site.tags}
                      creatable={true}
                      searchable
                      getCreateLabel={(query) => `+ Create "${query}"`}
                      onCreate={(newTag) => {
                        const newTagList = [...allTags, newTag];
                        setAllTags(newTagList);
                        return newTag;
                      }}
                    />
                    <FileInput
                      className={styles.settingFileInput}
                      label="Upload new Website Icon"
                      aria-label="Upload new Website Icon"
                      placeholder="Website Icon"
                    />
                    <Button className={styles.saveButton}>
                      Save Settings
                    </Button>
                  </div>
                  <div className={styles.mobileSettings}>
                  <h3>Advanced Settings</h3>
                  <Select
                    label="Audit Engine"
                    placeholder="Select Engine"
                    data={['WCAG 2.1 AA', 'WCAG Vially AA Windows 10 Laptop 15-inch firefox']}
                    className={styles.settingSelect}
                  />
                  <Select
                    label="Audit Speed"
                    placeholder="Choose Speed"
                    data={['Fast Audit', 'Standard Audit']}
                    className={styles.settingSelect}
                  />
                  <FileInput
                    className={styles.settingFileInput}
                    label="Upload new Website Icon"
                    aria-label="Upload new Website Icon"
                    placeholder="Website Icon"
                  />
                  <Button className={styles.saveButton}>
                    Save Advanced Settings
                  </Button>
                  </div>
                </>
                )}
              </div>
            ))}
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th></th>
                <th>Favicon </th>
                <th>Name <ChevronDown/></th>
                <th>Website <ChevronDown/></th>
                {/* <th>URL <ChevronDown/></th> */}
                <th>Action </th>
              </tr>
            </thead>
            <tbody>
              {filteredWebsites.map((site) => (
                <React.Fragment key={site.id}>
                  <tr>
                    <td onClick={() => toggleRow(site.id)}>
                      <div>
                        <UnstyledButton className={styles.circelBorder} style={expandedRows.includes(site.id) ? { backgroundColor: "#5b5eff", color: "white" } : undefined} aria-label={`Click here for more settings about 
                            ${site?.name}`} >
                          {expandedRows.includes(site.id) ? <ChevronUp/> : <ChevronDown />}
                        </UnstyledButton>
                      </div>
                    </td>
                    <td>
                      <img src={site.favicon} alt="favicon" className={styles.favicon} />
                    </td>
                    <td>{site.homepage}</td>
                    <td>{site.name}</td>
                    {/* <td>
                      <a href={site.url} target="_blank" rel="noopener noreferrer" className={styles.urlLink}>
                        {site.url}
                      </a>
                    </td> */}
                    <td>
                      <Button variant="outline" color="gray">
                        Audit
                      </Button>
                    </td>
                  </tr>

                  {expandedRows.includes(site.id) && (
                    <tr className={styles.expandedRow}>
                      <td colSpan={7}>
                        <div className={styles.settings}>
                          <strong>{site.homepage} Settings</strong>

                          <div className={styles.sectionWrapper}>
                            <Accordion
                              chevronPosition="left"
                              variant="separated"
                              defaultValue="Settings"
                              classNames={{ chevron: styles.chevron }}
                              radius={'lg'}
                              multiple={false}
                            >
                              <Accordion.Item value="Settings" key="Settings">
                                <Accordion.Control>
                                  Settings
                                </Accordion.Control>
                                <Accordion.Panel>
                                  <div className={styles.detailsSection}>
                                    <TextInput
                                      label="Name"
                                      placeholder="Edit website name"
                                      defaultValue={site.homepage}
                                      className={styles.settingSelect}
                                    />
                                    <Button variant="outline" className={styles.settingButton}>
                                      Rescan {site.homepage}
                                    </Button>
                                    {/* <Button variant="outline" className={styles.settingButton}>
                                      Manage Sitemap
                                    </Button> */}
                                {/* <MultiSelect
                                        data={tags}
                                        placeholder="Select tags"
                                        label="Your favorite frameworks"
                                        searchable
                                        creatable
                                        getCreateLabel={(query) => `+ Add ${query}`}
                                        onCreate={(query) => setTags((current) => [...current, query])}
                                        value={tags}
                                        onChange={setTags}
                                      /> */}

                                    <Box sx={{  padding: '20px', backgroundColor: '#fff' }}>
                                      <Flex justify={"space-between"} align={"center"}>
                                          <Text size="lg" weight={700}>All Labels</Text>
                                          <Button variant="light" style={{ margin: '10px 0' }} onClick={() => setModalOpen(true)}>Add new label</Button>
                                      </Flex>
                                      {tags.map(tag => (
                                        <Group key={tag.id} position="apart" style={{ margin: '10px 0' }}>
                                          {editTagId === tag.id ? (
                                            <TextInput
                                              autoFocus
                                              value={editTagName}
                                              onChange={(event) => setEditTagName(event.currentTarget.value)}
                                              onKeyDown={(event) => handleKeyDown(event, tag.id)}
                                              onBlur={() => stopEdit()} // Optional: stop editing on blur
                                            />
                                          ) : (
                                            <Text>{tag.name}</Text>
                                          )}
                                          <Menu withinPortal position="bottom-end">
                                            <Menu.Target>
                                              <Button>⋮</Button>
                                            </Menu.Target>
                                            <Menu.Dropdown>
                                              <Menu.Item onClick={() => startEdit(tag)}>Rename</Menu.Item>
                                              {/* <Menu.Item onClick={() => console.log('View users with tag')}>View users with tag</Menu.Item> */}
                                              {/* <Menu.Item onClick={() => console.log('View tag usage')}>View tag usage</Menu.Item> */}
                                              <Menu.Item color="red" onClick={() => deleteTag(tag.id)}>Delete</Menu.Item>
                                            </Menu.Dropdown>
                                          </Menu>
                                        </Group>
                                      ))}

                                          <Modal
                                            opened={isModalOpen}
                                            onClose={() => setModalOpen(false)}
                                            title="Add a New Label"
                                            zIndex={1000}
                                          >
                                              {/* <Group align={'center'} position={'center'} p={28}> */}
                                                  <TextInput
                                                    label="Label Name"
                                                    placeholder="Enter a new Label name"
                                                    value={newTagName}
                                                    onChange={(event) => setNewTagName(event.currentTarget.value)}
                                                    onKeyPress={(event) => {
                                                      if (event.key === 'Enter') {
                                                        addTag();
                                                      }
                                                    }}
                                                  />
                                                  <Button onClick={addTag} style={{ marginTop: 10 }}>
                                                    Add Label
                                                  </Button>
                                              {/* </Group> */}
                                          </Modal>
                                        </Box>
                                      

                                        <MultiSelect
                                            label="Audit Standars"
                                            data={['2.1A' , '2.1AA' , '2.1AAA' , '2.2A' , '2.2AA' , '2.2AAA']} 
                                            placeholder="Select Audit Standars"
                                            // value={selectedTags}
                                            // onChange={}
                                            style={{ marginTop: '10px', marginBottom: '10px' }}
                                          />
                                          <Checkbox
                                            className={styles.notifyCheckbox}
                                            label="Send Email Notification When Audit Start or Complete"
                                            checked={notifications}
                                            onChecked={(e) => setNotifications(e.target.checked)}
                                          />
                                    <Button className={styles.saveButton}>
                                      Save Settings
                                    </Button>
                                  </div>
                                </Accordion.Panel>
                              </Accordion.Item>

                              <Accordion.Item value="Advanced Settings" key="Advanced Settings">
                                <Accordion.Control>
                                  Advanced Settings
                                </Accordion.Control>
                                <Accordion.Panel>
                                  <div className={styles.detailsSection}>


                                  <Accordion value={expandedSections} 
                                    onChange={setExpandedSections}
                                    chevronPosition="left"
                                    variant="separated"
                                    defaultValue="Settings"
                                    classNames={{ chevron: styles.chevron }}
                                    radius={'lg'}
                                    multiple={false}
                                  >
                                      <Accordion.Item value="auditSettings">
                                        <Accordion.Control onClick={() => handleToggleSection('auditSettings')}>
                                          Configure Automated Audit Settings
                                        </Accordion.Control>
                                        <Accordion.Panel>
                                          <div style={{ paddingLeft: "20px" }}>
                                            <Select
                                              label="Audit Engine"
                                              placeholder="Select Engine"
                                              data={['WCAG 2.1 AA', 'WCAG Vially AA Windows 10 Laptop 15-inch firefox']}
                                              className={styles.settingSelect}
                                            />
                                            <Select
                                              label="Audit Speed"
                                              placeholder="Choose Speed"
                                              data={['Fast Audit', 'Standard Audit']}
                                              className={styles.settingSelect}
                                            />
                                            <NumberInput
                                              label="Scan Rate (ms):"
                                              description="Defines the frequency at which the system performs scans (in milliseconds)."
                                              value={scanRate}
                                              onChange={(NewVal)=>setScanRate(NewVal)}
                                              min={100}  // Minimum scan rate to avoid too frequent scans
                                            />
                                            <NumberInput
                                              label="Delay Scan (ms):"
                                              description="Sets a delay before the first scan starts (in milliseconds)."
                                              value={scanDelay}
                                              onChange={(NewVal)=>setScanDelay(NewVal)}
                                              min={0}  // No negative delays
                                          />
                                          </div>
                                        </Accordion.Panel>
                                      </Accordion.Item>

                                      <Accordion.Item value="pageReadiness">
                                        <Accordion.Control onClick={() => handleToggleSection('pageReadiness')}>Page Readiness Settings</Accordion.Control>
                                        <Accordion.Panel>
                                          <div style={{ paddingLeft: "20px" }}>
                                            <Tooltip label="Defines a delay before the system begins analysis to allow all scripts and content to fully load.">
                                              <div>
                                                <TextInput
                                                  label="Wait time after page load (ms)"
                                                  type="number"
                                                  value={waitAfterLoad}
                                                  onChange={(event) => setWaitAfterLoad(Number(event.currentTarget.value))}
                                                />
                                              </div>
                                            </Tooltip>
                                            <Tooltip label="Sets how frequently the system checks for active network connections to determine page readiness.">
                                              <div>
                                                <TextInput
                                                  label="Polling interval used by all network checks (in milliseconds)"
                                                  type="number"
                                                  value={pollInterval}
                                                  onChange={(event) => setPollInterval(Number(event.currentTarget.value))}
                                                />
                                              </div>
                                            </Tooltip>
                                            <Tooltip label="Specifies the maximum number of new network connections that can be initiated before considering the page as ready.">
                                              <div>
                                                <NumberInput
                                                  label="Allowed number of new connections"
                                                  value={newConnections}
                                                  onChange={(newVal) => setNewConnections(newVal)}
                                                />
                                              </div>
                                            </Tooltip>
                                            <Tooltip label="Sets the threshold of open connections that are considered acceptable for page readiness.">
                                              <div>
                                                <NumberInput
                                                  label="Allowed number of open connections"
                                                  value={openConnections}
                                                  onChange={(newVal) => setOpenConnections(newVal)}
                                                />
                                              </div>
                                            </Tooltip>
                                            <Tooltip label="When enabled, allows the system to treat the page as ready when the document state is 'interactive'. This is experimental and may lead to unpredictable results.">
                                              <div>
                                                <Checkbox
                                                className={styles.notifyCheckbox}
                                                  label="Allow interactive ready state (experimental)"
                                                  checked={allowInteractive}
                                                  onChecked={(event) => setAllowInteractive(event.currentTarget.checked)}
                                                />
                                              </div>
                                            </Tooltip>
                                          </div>
                                        </Accordion.Panel>
                                      </Accordion.Item>
                                      

                                      <Accordion.Item value="sitemapSettings">
                                        <Accordion.Control onClick={() => handleToggleSection('sitemapSettings')}>Sitemap Settings</Accordion.Control>
                                        <Accordion.Panel> 
                                          <div style={{paddingLeft:"20px"}}>
                                              <Textarea
                                                placeholder={'URLs to exclude'}
                                                label="URLs to exclude:"
                                                value={urlsToExclude}
                                                onChange={(event) => setUrlsToExclude(event.currentTarget.value)}
                                              />
                                              <Textarea
                                                placeholder={'Must not have'}
                                                label="Must not have:"
                                                value={mustNotHave}
                                                onChange={(event) => setMustNotHave(event.currentTarget.value)}
                                              />
                                              <Textarea
                                                placeholder={'Must have all'}
                                                label="Must have all:"
                                                value={mustHaveAll}
                                                onChange={(event) => setMustHaveAll(event.currentTarget.value)}
                                              />
                                              <Textarea
                                                placeholder={'Must have any'}
                                                label="Must have any:"
                                                value={mustHaveAny}
                                                onChange={(event) => setMustHaveAny(event.currentTarget.value)}
                                              />
                                              <Textarea
                                                label="Media types to exclude (comma-separated, e.g., .jpg, .png):"
                                                value={mediaToExclude}
                                                onChange={(event) => setMediaToExclude(event.currentTarget.value)}
                                              />
                                              <Textarea
                                                label="Media types to include (comma-separated, e.g., .mp4, .pdf):"
                                                value={mediaToInclude}
                                                onChange={(event) => setMediaToInclude(event.currentTarget.value)}
                                              />
                                              <Checkbox
                                                className={styles.notifyCheckbox}
                                                label="Ignore case on values for the below excludes and includes"
                                                onChecked={(event) => setIgnoreCase(event.currentTarget.checked)}
                                                checked={ignoreCase}
                                              // onChange={}
                                              />
                                              <Checkbox
                                                className={styles.notifyCheckbox}
                                                label="Respect robots.txt rules"
                                                onChecked={(event) => setRobotOption(event.currentTarget.checked)}
                                                checked={robotOption}
                                              />
                                          </div>
                                        </Accordion.Panel>
                                      </Accordion.Item>


                                      <Accordion.Item value="uniquePageSettings">
                                        <Accordion.Control onClick={() => handleToggleSection('uniquePageSettings')}>Unique Page Settings</Accordion.Control>
                                        <Accordion.Panel>
                                          <div style={{paddingLeft:"20px"}}>
                                            <Checkbox label="Use URL to determine unique page" checked={useUrl} className={styles.notifyCheckbox} onChecked={(e) => setUseUrl(e.currentTarget.checked)} />
                                            <Checkbox label="Use the page content to determine unique page" checked={useContent} className={styles.notifyCheckbox} onChecked={(e) => setUseContent(e.currentTarget.checked)} />
                                            <Checkbox label="Discard fragment identifiers when determining unique URLs" checked={discardFragments} className={styles.notifyCheckbox} onChecked={(e) => setDiscardFragments(e.currentTarget.checked)} />
                                            <Checkbox label="Discard all URL parameters when determining unique URLs" checked={discardParameters} className={styles.notifyCheckbox} onChecked={(e) => setDiscardParameters(e.currentTarget.checked)} />
                                            <Textarea label="URL Parameters to use when determining unique URLs:" value={parametersToUse} onChange={(e) => setParametersToUse(e.currentTarget.value)} />
                                            <Textarea label="URL Parameters to discard when determining unique URLs:" value={parametersToDiscard} onChange={(e) => setParametersToDiscard(e.currentTarget.value)} />
                                            <Checkbox label="Use the built-in hash function:" checked={useHashFunction} className={styles.notifyCheckbox} onChecked={(e) => setUseHashFunction(e.currentTarget.checked)} />
                                            <Textarea label="Javascript to produce content hash:" value={customHashFunction} onChange={(e) => setCustomHashFunction(e.currentTarget.value)} />
                                          </div>
                                          </Accordion.Panel>
                                      </Accordion.Item>

                                      <Accordion.Item value="pageAnalysisSettings">
                                        <Accordion.Control onClick={() => handleToggleSection('pageAnalysisSettings')}> Page Analysis Settings</Accordion.Control>
                                        <Accordion.Panel>
                                            <div style={{ paddingLeft: "20px" }}>
                                                <TextInput
                                                  label="Javascript to produce unique title:"
                                                  value={javascriptCode}
                                                  onChange={(event) => setJavascriptCode(event.currentTarget.value)}
                                                />
                                                <TextInput
                                                  label="Referrer:"
                                                  value={referrer}
                                                  onChange={(event) => setReferrer(event.currentTarget.value)}
                                                />
                                                <Select
                                                  label="Restrict scanned domains to:"
                                                  data={['Domains defined in the domains list']}
                                                  value={restrictDomains}
                                                  onChange={setRestrictDomains}
                                                  placeholder="Select domain restrictions"
                                                  multiple
                                                />
                                                <Select
                                                  label="Default Pages:"
                                                  data={['index.htm', 'index.html']}
                                                  value={defaultPages}
                                                  onChange={setDefaultPages}
                                                />
                                                <NumberInput
                                                  label="Maximum number of pages:"
                                                  value={maxPages}
                                                  onChange={(newVal)=>setMaxPages(newVal)}
                                                />
                                                <Checkbox
                                                  className={styles.notifyCheckbox}
                                                  label="Store Passes and Inapplicables:"
                                                  checked={storePasses}
                                                  onChecked={(event) => setStorePasses(event.currentTarget.checked)}
                                                />
                                                <Checkbox
                                                  className={styles.notifyCheckbox}
                                                  label="Run browser in 'Headless' mode:"
                                                  checked={headlessMode}
                                                  onChecked={(event) => setHeadlessMode(event.currentTarget.checked)}
                                                />
                                            </div>
                                          </Accordion.Panel>
                                      </Accordion.Item>
                                  </Accordion>
                                  
                                    <FileInput
                                      className={styles.settingFileInput}
                                      label="Upload new Website Icon"
                                      aria-label="Upload new Website Icon"
                                      placeholder="Website Icon"
                                    />
                                    <Button className={styles.saveButton}>
                                      Save Advanced Settings
                                    </Button>
                                  </div>
                                </Accordion.Panel>
                              </Accordion.Item>

                              <Accordion.Item value="Sitemap" key="Sitemap">
                                <Accordion.Control>
                                  Sitemap
                                </Accordion.Control>
                                <Accordion.Panel>
                                  <div style={{textAlign:"center"}}>
                                    <SitemapTable  siteID={'blank-example.netlify.app'}  />
                                  </div>
                                </Accordion.Panel>
                              </Accordion.Item>
                            </Accordion>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default ManageWebsite;
